import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  CampaignExcludedContact,
  CampaignExcludedSegment,
  CampaignList,
  CampaignManualRecipient,
  CampaignMetrics,
  CampaignStatus,
  MarketingCampaign,
  campaignStatusLabel,
} from '../models/marketing-campaign.model';
import { Segment } from '../../segments/models/segment.model';
import { CampaignShippingComponent } from '../campaign-shipping/campaign-shipping.component';

export type CampaignTab = 'overview' | 'metrics' | 'audience' | 'content';

/**
 * Cuerpo de pestañas del detalle de un correo de marketing (Inc 29 / 36).
 *
 * Componente PRESENTACIONAL: no carga datos ni ejecuta acciones — recibe todo
 * por @Input desde el contenedor (hoy `campaign-detail-modal`). Sigue el
 * precedente de `tickets/tickets-detail-tabs/`, de modo que el HTML de las
 * pestañas vive en UN solo sitio y puede reutilizarse desde cualquier chrome
 * (modal o página).
 *
 * Inc 36: la pestaña "Envío" desapareció. La tabla de destinatarios
 * (`app-campaign-shipping`) vive ahora DENTRO de "Métricas", debajo de los KPIs
 * de Entrega y Engagement — que son la fuente única de los números.
 */
@Component({
  selector: 'app-campaign-detail-tabs',
  standalone: true,
  imports: [CommonModule, CampaignShippingComponent],
  templateUrl: './campaign-detail-tabs.component.html',
})
export class CampaignDetailTabsComponent {
  private readonly sanitizer = inject(DomSanitizer);

  @Input({ required: true }) campaign!: MarketingCampaign;
  @Input() metrics: CampaignMetrics | null = null;
  @Input() lists: CampaignList[] = [];
  @Input() segments: Segment[] = [];
  @Input() manualRecipients: CampaignManualRecipient[] = [];
  @Input() excludedContacts: CampaignExcludedContact[] = [];
  @Input() excludedSegments: CampaignExcludedSegment[] = [];
  @Input() activeTab: CampaignTab = 'overview';

  @Output() tabChange = new EventEmitter<CampaignTab>();
  /** El usuario pidió ir al editor de contenido desde la pestaña "Contenido". */
  @Output() editRequested = new EventEmitter<void>();

  showHtmlPreview = false;

  readonly tabs: { key: CampaignTab; label: string }[] = [
    { key: 'overview',  label: 'Detalle'   },
    { key: 'metrics',   label: 'Métricas'  },
    { key: 'audience',  label: 'Audiencia' },
    { key: 'content',   label: 'Contenido' },
  ];

  setTab(tab: CampaignTab): void {
    this.tabChange.emit(tab);
  }

  /** Expone bodyHtml como SafeHtml para usarlo con [srcdoc] en iframe sandboxed. */
  get trustedBodyHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.campaign?.bodyHtml ?? '');
  }

  togglePreview(): void { this.showHtmlPreview = !this.showHtmlPreview; }

  // ===== Helpers =====
  formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' });
  }

  segmentName(idSegment: number): string {
    const s = this.segments.find(x => x.id === idSegment);
    return s ? s.name : `Segmento #${idSegment}`;
  }

  segmentTotal(idSegment: number): number {
    const s = this.segments.find(x => x.id === idSegment);
    return s ? s.totalContacts : 0;
  }

  channelLabel(type: string | null | undefined): string {
    if (!type) return 'canal predeterminado';
    const map: Record<string, string> = {
      USER_GMAIL: 'Gmail', USER_MICROSOFT: 'Outlook',
      USER_SMTP: 'SMTP', USER_API: 'API', SYSTEM_API: 'Sistema',
    };
    return map[type] || type;
  }

  statusBadgeClass(status: CampaignStatus): string {
    const map: Record<CampaignStatus, string> = {
      DRAFT: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      SCHEDULED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      SENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      SENT: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
      CANCELLED: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
      FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    };
    return map[status] || map.DRAFT;
  }

  statusLabel(status: CampaignStatus): string {
    return campaignStatusLabel(status);
  }

  get progressPercent(): number {
    const total = this.metrics?.totalRecipients ?? this.campaign?.totalRecipients ?? 0;
    if (total === 0) return 0;
    const sent = this.metrics?.sent ?? this.campaign?.sentCount ?? 0;
    return Math.round((sent / total) * 100);
  }

  /** Formatea rate (0..1) → "32.06%". null → "—" */
  formatRate(rate: number | null | undefined): string {
    if (rate === null || rate === undefined) return '—';
    return (rate * 100).toFixed(2) + '%';
  }
}
